import React, { useCallback, memo, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { media, sectionStyles, contentStyles, mainContentStyles } from '../../styles/media';
import { Icon } from '../common/icons/IconSprite';
import { Description } from '../common/Description';
import { Title } from '../common/Title';

const BenefitsSection = styled.section`
  ${sectionStyles}
  background: ${props => props.theme.colors.background.secondary};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 75, 143, 0.05) 0%, transparent 50%, rgba(0, 184, 212, 0.05) 100%);
    pointer-events: none;
  }

  ${media.md`
    padding-top: 60px;
  `}

  ${media.sm`
    padding-top: 40px;
  `}
`;

const MainContent = styled.div`
  ${mainContentStyles}
`;

const Content = styled.div`
  ${contentStyles}
  text-align: center;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s ease forwards;
  
  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  ${media.lg`
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    max-width: 800px;
  `}
  
  ${media.md`
    gap: 20px;
    padding: 0 16px;
  `}
  
  ${media.sm`
    grid-template-columns: 1fr;
    max-width: 360px;
  `}
`;

// Общие стили для повторяющихся эффектов
const commonTransition = css`
  transition: all 0.3s ease;
`;

const commonHoverEffect = css`
  &:hover {
    transform: translateY(-2px);
  }
`;

const Card = styled.div`
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  border-radius: 24px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  ${commonTransition}
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.6s ease forwards;
  animation-delay: ${props => props.$index * 0.1}s;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #1ABC9C 0%, #FF4B8F 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(43, 45, 66, 0.1);
    
    &::before {
      opacity: 1;
    }
  }
  
  ${media.lg`
    padding: 24px;
  `}
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => {
    switch(props.$index % 4) {
      case 0: return 'rgba(26, 188, 156, 0.1)';
      case 1: return 'rgba(255, 75, 143, 0.1)';
      case 2: return 'rgba(243, 156, 18, 0.1)';
      case 3: return 'rgba(241, 196, 15, 0.1)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  
  svg {
    width: 24px;
    height: 24px;
    color: ${props => {
      switch(props.$index % 4) {
        case 0: return '#1ABC9C';
        case 1: return '#FF4B8F';
        case 2: return '#F39C12';
        case 3: return '#F1C40F';
      }
    }};
  }
  
  ${media.lg`
    width: 40px;
    height: 40px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  `}
`;

const BenefitTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #2B2D42;
  margin: 0;
  line-height: 1.3;
  text-align: center;
`;

const BenefitDescription = styled.p`
  font-size: clamp(14px, 1.5vw, 16px);
  line-height: 1.6;
  color: #666;
  margin: 0;
  text-align: center;
  flex-grow: 1;
`;

// Мемоизированная карточка преимущества
const BenefitCard = memo(({ icon, title, description, index }) => (
  <Card $index={index}>
    <IconWrapper $index={index}>
      <Icon name={icon} />
    </IconWrapper>
    <BenefitTitle>{title}</BenefitTitle>
    <BenefitDescription>{description}</BenefitDescription>
  </Card>
));

BenefitCard.displayName = 'BenefitCard';

const StyledTitle = styled(Title)`
  h2 {
    font-size: 48px;
    font-weight: 600;
    color: ${props => props.theme.colors.text.heading};
    margin: 0;
    line-height: 1.2;
    
    ${media.md`
      font-size: 32px;
    `}
    
    ${media.sm`
      font-size: 28px;
      word-break: break-word;
      padding-inline: 10px;
    `}
  }
`;

const Benefits = () => {
  const { t } = useTranslation();

  const benefits = useMemo(() => [
    {
      icon: 'security',
      title: t('benefits.items.first.title'),
      description: t('benefits.items.first.description')
    },
    {
      icon: 'flexibility',
      title: t('benefits.items.third.title'),
      description: t('benefits.items.third.description')
    },
    {
      icon: 'dispute',
      title: t('benefits.items.fourth.title'),
      description: t('benefits.items.fourth.description')
    },
    {
      icon: 'transparency',
      title: t('benefits.items.fifth.title'),
      description: t('benefits.items.fifth.description')
    },
    {
      icon: 'marketplace',
      title: t('benefits.items.sixth.title'),
      description: t('benefits.items.sixth.description')
    },
    {
      icon: 'rewards',
      title: t('benefits.items.seventh.title'),
      description: t('benefits.items.seventh.description')
    }
  ], [t]);

  return (
    <BenefitsSection id="about">
      <MainContent>
        <Content>
          <Title $inlineOnMobile>
            <h2>{t('benefits.title.line1')} {t('benefits.title.line2')} </h2>
            <h2><span className="highlight">{t('benefits.title.highlight')}</span></h2>
          </Title>
          <Description>{t('benefits.description')}</Description>
          <BenefitsGrid>
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                index={index}
                {...benefit}
              />
            ))}
          </BenefitsGrid>
        </Content>
      </MainContent>
    </BenefitsSection>
  );
};

export default memo(Benefits);